import { Viewer } from '@/components';
import FullPageError from '@/components/FullPageError';
import FullPageLoader from '@/components/FullPageLoader';
import { ViewerSettingsDialog } from '@/components/ViewerSettingsDialog';
import { buildOrgUrl, isAppDomain } from '@/core/domain';
import { execute } from '@/graphql/execute';
import { iAction } from '@/layout/Skeleton';
import PageSkeleton from '@/layout/Skeleton';
import { getSharedProjectQuery } from '@/queries/project.query';
import { CogFourSolid, LayersThree } from '@mynaui/icons-react';
import { useQuery } from '@tanstack/react-query';
import { FunctionComponent, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams, useSearchParams } from 'react-router-dom';

interface iSharedPageProps {}

const SharedPage: FunctionComponent<iSharedPageProps> = () => {
  const { projectId } = useParams();
  if (!projectId) throw new Error('Token is required');

  const [params, setParams] = useSearchParams();
  const [isSettingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const isLayersEnabled = params.get('layers') === 'true';

  const { data, isLoading, error } = useQuery({
    queryKey: ['sharedProject', projectId],
    queryFn: () => execute(getSharedProjectQuery),
    retry: false,
  });

  const project = data?.sharedProject;

  useEffect(() => {
    if (!project?.organization?.slug) return;
    if (!isAppDomain()) return;
    const path = `/shared/${projectId}${window.location.search}`;
    window.location.href = buildOrgUrl(project.organization.slug, path);
  }, [project?.organization?.slug, projectId]);

  const actions: iAction[] = [
    {
      icon: <LayersThree />,
      onClick: () => {
        const newParams = new URLSearchParams(window.location.search);
        newParams.set('layers', isLayersEnabled ? 'false' : 'true');
        setParams(newParams);
      },
      tooltip: 'Toggle layers',
      appearance: isLayersEnabled ? 'primary' : 'secondary',
    },
    {
      icon: <CogFourSolid />,
      onClick: () => setSettingsDialogOpen(true),
      tooltip: 'Viewer Settings',
      appearance: 'secondary',
    },
  ];

  if (isLoading) return <FullPageLoader />;
  if (error) {
    return (
      <FullPageError
        error={error}
        title='Failed to load project'
        subtitle='If the issue persists, please your administrator.'
      />
    );
  }
  return (
    <div className='h-full flex flex-col'>
      <PageSkeleton
        hideSidebar
        steps={[{ label: project?.name || 'Loading...', href: `/shared/${projectId}` }]}
        actions={actions}>
        <div className='flex-1 flex flex-col'>
          <ErrorBoundary key={project?.id} fallback={<div>Error</div>}>
            <Viewer projectId={project?.id!} />
          </ErrorBoundary>
        </div>
      </PageSkeleton>
      {isSettingsDialogOpen && (
        <ViewerSettingsDialog open={isSettingsDialogOpen} onOpenChange={setSettingsDialogOpen} />
      )}
    </div>
  );
};

export default SharedPage;
